import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  cloudGetMe,
  cloudGetTenantInfo,
  cloudLogin,
  ensureCloudSession,
  getCloudToken,
  setCloudToken,
  setSessionPassword,
} from "../api/client";
import {
  DEFAULT_ORG,
  orgFromTenant,
  type Organization,
} from "../config/org";
import { needsPhoneBind } from "./phoneGate";
import { clearPhoneBindNudgeDismiss } from "./phoneBindNudgeStorage";

const NAME_KEY = "yunshu.user_name";

interface AuthState {
  isAuthenticated: boolean;
  email: string | null;
  /** Display name (WeChat nickname / registered name) */
  name: string | null;
  role: string | null;
  tenantId: string | null;
  org: Organization;
  phoneBound: boolean | null;
  wxBound: boolean;
  /** 微信账号且未绑手机 → 必须先完成绑定才能进控制台 */
  needsPhoneBind: boolean;
  login: (email: string, password: string) => boolean | Promise<boolean>;
  /** JWT already set (e.g. WeChat QR); hydrate local session. */
  loginWithToken: (payload: {
    accessToken: string;
    user?: {
      email: string;
      name?: string;
      role: string;
      tenantId: string;
      tenantName?: string;
      phoneBound?: boolean;
      wxBound?: boolean;
    } | null;
    tenant?: { id: string; name: string } | null;
  }) => Promise<boolean>;
  markPhoneBound: (phoneBound?: boolean) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

const STORAGE_KEY = "yunshu.auth";
const ORG_KEY = "yunshu.org";

function readStoredOrg(): Organization {
  try {
    const raw = localStorage.getItem(ORG_KEY);
    if (!raw) return DEFAULT_ORG;
    const parsed = JSON.parse(raw) as Organization;
    if (parsed?.id && parsed?.name && parsed?.shortName) return parsed;
  } catch {
    /* ignore */
  }
  return DEFAULT_ORG;
}

function persistOrg(org: Organization) {
  try {
    localStorage.setItem(ORG_KEY, JSON.stringify(org));
  } catch {
    /* ignore */
  }
}

function persistName(name: string | null) {
  try {
    if (name) localStorage.setItem(NAME_KEY, name);
    else localStorage.removeItem(NAME_KEY);
  } catch {
    /* ignore */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [email, setEmail] = useState<string | null>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const [name, setName] = useState<string | null>(() => {
    try {
      return localStorage.getItem(NAME_KEY);
    } catch {
      return null;
    }
  });

  const [role, setRole] = useState<string | null>(() => {
    try {
      return localStorage.getItem("yunshu.user_role");
    } catch {
      return null;
    }
  });

  const [tenantId, setTenantId] = useState<string | null>(() => {
    try {
      return localStorage.getItem("yunshu.tenant_id");
    } catch {
      return null;
    }
  });

  const [org, setOrg] = useState<Organization>(() =>
    email ? readStoredOrg() : DEFAULT_ORG,
  );

  const [phoneBound, setPhoneBound] = useState<boolean | null>(null);
  const [wxBound, setWxBound] = useState(false);

  useEffect(() => {
    const onCleared = () => {
      setEmail(null);
      setName(null);
      setRole(null);
      setTenantId(null);
      setPhoneBound(null);
      setWxBound(false);
      setOrg(DEFAULT_ORG);
      try {
        localStorage.removeItem(ORG_KEY);
        localStorage.removeItem(NAME_KEY);
      } catch {
        /* ignore */
      }
    };
    window.addEventListener("yunshu:auth-cleared", onCleared);
    return () => window.removeEventListener("yunshu:auth-cleared", onCleared);
  }, []);

  useEffect(() => {
    if (!email || getCloudToken()) return;
    void (async () => {
      const res = await ensureCloudSession(email);
      if (!res.ok) {
        localStorage.removeItem(STORAGE_KEY);
        setCloudToken(null);
        setSessionPassword(null);
        setEmail(null);
        setName(null);
        setPhoneBound(null);
        setWxBound(false);
        persistName(null);
      }
    })();
  }, [email]);

  // Refresh tenant + user display name from API when session is ready
  useEffect(() => {
    if (!email || !getCloudToken()) return;
    void cloudGetTenantInfo()
      .then((t) => {
        const next = orgFromTenant({ id: t.id, name: t.name });
        setOrg(next);
        persistOrg(next);
        setTenantId(t.id);
        try {
          localStorage.setItem("yunshu.tenant_id", t.id);
        } catch {
          /* ignore */
        }
      })
      .catch(() => {
        /* keep cached org */
      });
    void cloudGetMe()
      .then((me) => {
        if (me.name) {
          setName(me.name);
          persistName(me.name);
        }
        if (me.role) {
          setRole(me.role);
          try {
            localStorage.setItem("yunshu.user_role", me.role);
          } catch {
            /* ignore */
          }
        }
        setPhoneBound(Boolean(me.phoneBound));
        setWxBound(Boolean(me.wxBound));
      })
      .catch(() => {
        /* keep cached name */
      });
  }, [email]);

  const value = useMemo<AuthState>(
    () => ({
      isAuthenticated: Boolean(email),
      email,
      name,
      role,
      tenantId,
      org,
      phoneBound,
      wxBound,
      needsPhoneBind: needsPhoneBind({ email, phoneBound, wxBound }),
      login: async (nextEmail, password) => {
        if (!nextEmail.trim() || !password.trim()) return false;
        try {
          const result = await cloudLogin(nextEmail.trim(), password);
          localStorage.setItem(STORAGE_KEY, nextEmail.trim());

          if (result.user) {
            localStorage.setItem("yunshu.user_role", result.user.role);
            localStorage.setItem("yunshu.tenant_id", result.user.tenantId);
            setRole(result.user.role);
            setTenantId(result.user.tenantId);
            if (result.user.name) {
              setName(result.user.name);
              persistName(result.user.name);
            }
          }

          const tenantName =
            result.tenant?.name ||
            result.user?.tenantName ||
            result.user?.tenantId ||
            DEFAULT_ORG.name;
          const tenantIdNext =
            result.tenant?.id || result.user?.tenantId || DEFAULT_ORG.id;
          const nextOrg = orgFromTenant({
            id: tenantIdNext,
            name: tenantName,
          });
          setOrg(nextOrg);
          persistOrg(nextOrg);

          setEmail(nextEmail.trim());
          // 账密登录：手机绑定非强制；真实状态由 /auth/me 刷新
          setPhoneBound(null);
          setWxBound(false);
          clearPhoneBindNudgeDismiss();
          return true;
        } catch (err) {
          setCloudToken(null);
          console.error("Login failed:", err);
          return false;
        }
      },
      loginWithToken: async (payload) => {
        const user = payload.user;
        if (!user?.email) return false;
        try {
          localStorage.setItem(STORAGE_KEY, user.email);
          localStorage.setItem("yunshu.user_role", user.role);
          localStorage.setItem("yunshu.tenant_id", user.tenantId);
          setRole(user.role);
          setTenantId(user.tenantId);
          if (user.name) {
            setName(user.name);
            persistName(user.name);
          }
          const tenantName =
            payload.tenant?.name || user.tenantName || user.tenantId;
          const tenantIdNext = payload.tenant?.id || user.tenantId;
          const nextOrg = orgFromTenant({
            id: tenantIdNext,
            name: tenantName,
          });
          setOrg(nextOrg);
          persistOrg(nextOrg);
          if (typeof user.phoneBound === "boolean") {
            setPhoneBound(user.phoneBound);
          }
          if (typeof user.wxBound === "boolean") {
            setWxBound(user.wxBound);
          } else if (user.email.endsWith("@wechat.udhub.local")) {
            setWxBound(true);
          }
          setEmail(user.email);
          clearPhoneBindNudgeDismiss();
          return true;
        } catch {
          return false;
        }
      },
      markPhoneBound: (bound = true) => {
        setPhoneBound(bound);
      },
      logout: () => {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem("yunshu.user_role");
        localStorage.removeItem("yunshu.tenant_id");
        localStorage.removeItem(ORG_KEY);
        localStorage.removeItem(NAME_KEY);
        setCloudToken(null);
        setSessionPassword(null);
        setEmail(null);
        setName(null);
        setRole(null);
        setTenantId(null);
        setPhoneBound(null);
        setWxBound(false);
        setOrg(DEFAULT_ORG);
        clearPhoneBindNudgeDismiss();
      },
    }),
    [email, name, role, tenantId, org, phoneBound, wxBound],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
